/**
 * FLAGS — diálogo de confirmación de TODO toggle de feature (plataforma, org y
 * curso comparten este componente). Antes de aplicar el cambio muestra:
 *
 *  · las IMPLICACIONES estructuradas (navegación que desaparece, flujos de
 *    escritura bloqueados, y la garantía «los datos se conservan»);
 *  · los EFECTOS DE GRAFO: al deshabilitar, qué features dependientes quedan
 *    en pausa (aristas blandas) y qué violaciones DURAS lo bloquean; al
 *    habilitar, qué requisitos faltan;
 *  · el ciclo de vida (status/since) para decisiones de rollout informadas.
 *
 * Con violaciones duras el botón Confirmar se deshabilita: el server las
 * rechazaría igual (409/descartada) — el diálogo lo explica ANTES.
 */
import { type Component, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { AlertTriangle, Ban, EyeOff, PauseCircle, ShieldCheck } from "lucide-solid";
import { type FeatureGraphNode, type FeatureViolation, violationLabel } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

/** Forma mínima estructural de las implicaciones (API y manifest TS encajan). */
export interface ToggleImplications {
  navHidden?: string[];
  flowsBlocked?: string[];
}

export interface FeatureToggleRequest {
  key: string;
  name: string;
  nextEnabled: boolean;
  status?: string;
  since?: string;
  implications?: ToggleImplications;
  /** Violaciones DURAS que el cambio introduciría (bloquean Confirmar). */
  violations: FeatureViolation[];
  /** Nombres de dependientes blandos que quedarán en pausa al deshabilitar. */
  softAffected: string[];
}

export const FeatureToggleDialog: Component<{
  request: FeatureToggleRequest | null;
  graph: ReadonlyMap<string, FeatureGraphNode>;
  onConfirm: () => void;
  onCancel: () => void;
}> = (props) => {
  const { t } = useI18n();
  const req = () => props.request;
  const blocked = () => (req()?.violations.length ?? 0) > 0;
  const title = () => {
    const r = req();
    if (!r) {
      return "";
    }
    const { name } = r;
    return r.nextEnabled ? t(msg`Enable "${name}"?`) : t(msg`Disable "${name}"?`);
  };

  return (
    <Show when={req()}>
      {(r) => (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) {
              props.onCancel();
            }
          }}
          contentClass="max-w-[480px] p-6"
          role="alertdialog"
        >
          <DialogTitle class="mb-1 text-xl font-bold" data-testid="feature-toggle-title">
            {title()}
          </DialogTitle>
          <DialogDescription class="mb-4 text-sm text-muted-foreground">
            <span class="font-mono text-xs">{r().key}</span>
            <Show when={r().status}>
              {(s) => (
                <Badge variant="secondary" class="ml-2 align-middle">
                  {s()}
                </Badge>
              )}
            </Show>
            <Show when={r().since}>
              {(since) => {
                const sinceValue = since();
                return <span class="ml-2 text-xs">{t(msg`since ${sinceValue}`)}</span>;
              }}
            </Show>
          </DialogDescription>

          <div class="space-y-3 text-sm" data-testid="feature-toggle-implications">
            {/* Violaciones duras (bloquean) */}
            <Show when={blocked()}>
              <div
                class="rounded-lg border border-destructive/40 bg-destructive/10 p-3"
                data-testid="feature-toggle-blocked"
              >
                <p class="mb-1 flex items-center gap-2 font-semibold text-destructive">
                  <Ban class="h-4 w-4" /> {t(msg`This change is blocked by dependencies`)}
                </p>
                <ul class="list-inside list-disc space-y-0.5 text-destructive">
                  <For each={req()?.violations ?? []}>
                    {(v) => <li>{violationLabel(v, props.graph)}</li>}
                  </For>
                </ul>
                <p class="mt-1 text-xs text-destructive/80">
                  {r().nextEnabled
                    ? t(msg`Enable the required features first.`)
                    : t(msg`Disable the dependent features first.`)}
                </p>
              </div>
            </Show>

            {/* Dependientes blandos en pausa */}
            <Show when={!r().nextEnabled && r().softAffected.length > 0}>
              <div class="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
                <p class="mb-1 flex items-center gap-2 font-semibold text-amber-600 dark:text-amber-400">
                  <PauseCircle class="h-4 w-4" /> {t(msg`These features will pause meanwhile`)}
                </p>
                <ul class="list-inside list-disc space-y-0.5">
                  <For each={r().softAffected}>{(name) => <li>{name}</li>}</For>
                </ul>
              </div>
            </Show>

            {/* Implicaciones declaradas */}
            <Show when={!r().nextEnabled && r().implications?.navHidden?.length}>
              <div class="rounded-lg border border-border p-3">
                <p class="mb-1 flex items-center gap-2 font-semibold">
                  <EyeOff class="h-4 w-4 text-muted-foreground" />
                  {t(msg`Disappears from the app`)}
                </p>
                <ul class="list-inside list-disc space-y-0.5 text-muted-foreground">
                  <For each={r().implications?.navHidden ?? []}>{(item) => <li>{item}</li>}</For>
                </ul>
              </div>
            </Show>
            <Show when={!r().nextEnabled && r().implications?.flowsBlocked?.length}>
              <div class="rounded-lg border border-border p-3">
                <p class="mb-1 flex items-center gap-2 font-semibold">
                  <AlertTriangle class="h-4 w-4 text-muted-foreground" />
                  {t(msg`Blocked flows (writes only)`)}
                </p>
                <ul class="list-inside list-disc space-y-0.5 text-muted-foreground">
                  <For each={r().implications?.flowsBlocked ?? []}>{(item) => <li>{item}</li>}</For>
                </ul>
              </div>
            </Show>

            {/* Garantía de datos */}
            <p class="flex items-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck class="h-4 w-4 shrink-0 text-success" />
              {r().nextEnabled
                ? t(msg`Everything preserved while it was off comes back exactly as it was.`)
                : t(
                    msg`Nothing is deleted: existing data stays visible and comes back fully when re-enabled.`,
                  )}
            </p>
          </div>

          <div class="mt-5 flex justify-end gap-2">
            <Button variant="outline" onClick={() => props.onCancel()}>
              {t(msg`Cancel`)}
            </Button>
            <Button
              variant={r().nextEnabled ? "default" : "destructive"}
              disabled={blocked()}
              data-testid="feature-toggle-confirm"
              onClick={() => props.onConfirm()}
            >
              {r().nextEnabled ? t(msg`Enable`) : t(msg`Disable`)}
            </Button>
          </div>
        </Dialog>
      )}
    </Show>
  );
};
